import type { IBaseAttributes, IPresenceConstraint, TConstraints } from '@feathr/rachis';
import { Collection, Model } from '@feathr/rachis';

import { EIntegrationTypes } from './integrations';

export interface ITargeting extends IBaseAttributes {
  included: boolean;
  is_enabled: boolean;
  kind: string;
  name: string;
  parent: string;
  target_data?: string;
  integrations?: EIntegrationTypes[];
  /** Used to determine if a targeting should use AND (different group numbers) or OR (same group number) */
  group?: number;
}

export const AUDIENCE_TARGETING_KINDS = [
  'segment',
  'search',
  'lookalike',
  'email_list',
  'geofence',
  'geo_audience',
];

export const SLICE_TARGETING_KINDS = ['omni_awareness', 'omni_lookalike'];

export const FILTER_TARGETING_KINDS = ['age_demographic', 'interest', 'geo'];

export class Targeting extends Model<ITargeting> {
  public static readonly defaults: Partial<ITargeting> = {
    integrations: [EIntegrationTypes.Tradedesk],
  };
  public readonly className = 'Targeting';
  public get constraints(): TConstraints<ITargeting> {
    return {
      kind: {
        inclusion: [
          // Age demographic filter (or audience builder on reach pixl plus campaigns)
          'age_demographic',
          // Email mapping
          'email_list',
          // Geo filter (or audience builder on reach pixl plus campaigns)
          'geo',
          // Historical geofencing
          'geo_audience',
          // Mobile geofencing
          'geofence',
          // Interest filter
          'interest',
          // Affinity and lookalike (or audience builder on reach pixl plus campaigns)
          'lookalike',
          // Pixl plus awareness audience slice
          'omni_awareness',
          // Pixl plus lookalike audience slice
          'omni_lookalike',
          // Search keyword
          'search',
          // Group on retargeting, email mapping & pixl plus campaigns
          'segment',
          // Omni slice lookalike for deepening engagement
          'omni_lookalike',
        ],
      },
      target_data: {
        presence: (_, attributes) => {
          const { kind } = attributes;
          const config: IPresenceConstraint = { allowEmpty: false };
          switch (kind) {
            case 'segment':
              config.message =
                '^One of your targets does not have a group. Please choose or create a group.';
              break;

            case 'geo':
              config.message = '^One of your filters does not have a geographic area to filter on.';
              break;

            default:
              config.message = '^One of your targets is not fully configured.';
          }
          return config;
        },
      },
    };
  }

  public constructor(initialAttributes: Partial<ITargeting> = {}) {
    super(initialAttributes);
  }

  public override getDefaults(): Partial<ITargeting> {
    return Targeting.defaults;
  }
}

export class Targetings extends Collection<Targeting> {
  public getClassName(): string {
    return 'targetings';
  }

  public getModel(attributes: Partial<ITargeting>): Targeting {
    return new Targeting(attributes);
  }
}
